import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RuleForm = ({ 
  rule = null, 
  vendors = [], 
  onSave, 
  onCancel,
  className = '' 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    patterns: [''],
    patternType: 'fuzzy',
    vendorId: '',
    vendorName: '',
    confidenceThreshold: 0.8,
    priority: 1,
    isActive: true,
    caseSensitive: false,
    exactMatch: false
  });

  const [errors, setErrors] = useState({});
  const [testPattern, setTestPattern] = useState('');
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule?.name || '',
        patterns: rule?.patterns || [''],
        patternType: rule?.patternType || 'fuzzy',
        vendorId: rule?.vendorId || '',
        vendorName: rule?.vendorName || '',
        confidenceThreshold: rule?.confidenceThreshold || 0.8,
        priority: rule?.priority || 1,
        isActive: rule?.isActive !== undefined ? rule?.isActive : true,
        caseSensitive: rule?.caseSensitive || false,
        exactMatch: rule?.exactMatch || false
      });
    }
  }, [rule]);

  const vendorOptions = vendors?.map(vendor => ({
    value: vendor?.id,
    label: vendor?.name,
    description: vendor?.code ? `Code: ${vendor?.code}` : undefined
  }));

  const patternTypeOptions = [
    { value: 'fuzzy', label: 'Fuzzy Matching', description: 'Flexible text matching with typo tolerance' },
    { value: 'regex', label: 'Regular Expression', description: 'Advanced pattern matching with regex' },
    { value: 'exact', label: 'Exact Match', description: 'Precise text matching only' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handlePatternChange = (index, value) => {
    const newPatterns = [...formData?.patterns];
    newPatterns[index] = value;
    setFormData(prev => ({
      ...prev,
      patterns: newPatterns
    }));
  };

  const addPattern = () => {
    setFormData(prev => ({
      ...prev,
      patterns: [...prev?.patterns, '']
    }));
  };

  const removePattern = (index) => {
    if (formData?.patterns?.length > 1) {
      const newPatterns = formData?.patterns?.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        patterns: newPatterns
      }));
    }
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors?.find(c => c?.id === vendorId);
    setFormData(prev => ({
      ...prev,
      vendorId,
      vendorName: vendor ? vendor?.name : ''
    }));
  };

  const testPatternMatching = () => {
    if (!testPattern?.trim()) return;

    // Mock pattern testing logic
    const mockResults = {
      matches: formData?.patterns?.some(pattern => {
        if (formData?.patternType === 'exact') {
          return formData?.caseSensitive 
            ? testPattern === pattern 
            : testPattern?.toLowerCase() === pattern?.toLowerCase();
        } else if (formData?.patternType === 'regex') {
          try {
            const regex = new RegExp(pattern, formData.caseSensitive ? 'g' : 'gi');
            return regex?.test(testPattern);
          } catch {
            return false;
          }
        } else {
          // Fuzzy matching simulation
          const similarity = calculateSimilarity(testPattern?.toLowerCase(), pattern?.toLowerCase());
          return similarity >= formData?.confidenceThreshold;
        }
      }),
      confidence: Math.random() * 0.4 + 0.6, // Mock confidence between 0.6-1.0
      matchedPattern: formData?.patterns?.[0]
    };

    setTestResults(mockResults);
  };

  const calculateSimilarity = (str1, str2) => {
    // Simple similarity calculation for demo
    const longer = str1?.length > str2?.length ? str1 : str2;
    const shorter = str1?.length > str2?.length ? str2 : str1;
    
    if (longer?.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer?.length - editDistance) / longer?.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2?.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1?.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2?.length; i++) {
      for (let j = 1; j <= str1?.length; j++) {
        if (str2?.charAt(i - 1) === str1?.charAt(j - 1)) {
          matrix[i][j] = matrix?.[i - 1]?.[j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix?.[i - 1]?.[j - 1] + 1,
            matrix?.[i]?.[j - 1] + 1,
            matrix?.[i - 1]?.[j] + 1
          );
        }
      }
    }
    return matrix?.[str2?.length]?.[str1?.length];
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Rule name is required';
    }

    if (!formData?.patterns?.some(p => p?.trim())) {
      newErrors.patterns = 'At least one pattern is required';
    }

    if (!formData?.vendorId) {
      newErrors.vendorId = 'Vendor selection is required';
    }

    if (formData?.confidenceThreshold < 0.1 || formData?.confidenceThreshold > 1) {
      newErrors.confidenceThreshold = 'Confidence threshold must be between 0.1 and 1.0';
    }

    if (formData?.priority < 1 || formData?.priority > 100) {
      newErrors.priority = 'Priority must be between 1 and 100';
    }

    // Validate regex patterns if regex type is selected
    if (formData?.patternType === 'regex') {
      const invalidPatterns = formData?.patterns?.filter(pattern => {
        if (!pattern?.trim()) return false;
        try {
          new RegExp(pattern);
          return false;
        } catch {
          return true;
        }
      });

      if (invalidPatterns?.length > 0) {
        newErrors.patterns = 'Invalid regular expression pattern(s)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const cleanedData = {
        ...formData,
        patterns: formData?.patterns?.filter(p => p?.trim())
      };
      onSave(cleanedData);
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {rule ? 'Edit Matching Rule' : 'Create New Rule'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            iconName="X"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            
            <Input
              label="Rule Name"
              type="text"
              placeholder="Enter descriptive rule name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Select
              label="Vendor"
              placeholder="Select vendor to match"
              options={vendorOptions}
              value={formData?.vendorId}
              onChange={handleVendorChange}
              error={errors?.vendorId}
              searchable
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Priority"
                type="number"
                placeholder="1-100"
                value={formData?.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e?.target?.value) || 1)}
                error={errors?.priority}
                min={1}
                max={100}
                required
              />

              <Input
                label="Confidence Threshold"
                type="number"
                placeholder="0.1-1.0"
                value={formData?.confidenceThreshold}
                onChange={(e) => handleInputChange('confidenceThreshold', parseFloat(e?.target?.value) || 0.8)}
                error={errors?.confidenceThreshold}
                min={0.1}
                max={1.0}
                step={0.1}
                required
              />
            </div>
          </div>

          {/* Pattern Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Pattern Configuration</h3>
            
            <Select
              label="Pattern Type"
              options={patternTypeOptions}
              value={formData?.patternType}
              onChange={(value) => handleInputChange('patternType', value)}
              required
            />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">
                  Matching Patterns {errors?.patterns && <span className="text-error">*</span>}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPattern}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Pattern
                </Button>
              </div>

              {formData?.patterns?.map((pattern, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    type="text"
                    placeholder={
                      formData?.patternType === 'regex' ?'Enter regex pattern' :'Enter vendor name pattern'
                    }
                    value={pattern}
                    onChange={(e) => handlePatternChange(index, e?.target?.value)}
                    className="flex-1"
                  />
                  {formData?.patterns?.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePattern(index)}
                      iconName="Trash2"
                    />
                  )}
                </div>
              ))}
              
              {errors?.patterns && (
                <p className="text-sm text-error">{errors?.patterns}</p>
              )}
            </div>

            {/* Pattern Options */}
            <div className="space-y-3">
              <Checkbox
                label="Case Sensitive"
                description="Match patterns with exact case sensitivity"
                checked={formData?.caseSensitive}
                onChange={(e) => handleInputChange('caseSensitive', e?.target?.checked)}
              />

              {formData?.patternType !== 'regex' && (
                <Checkbox
                  label="Exact Match Only"
                  description="Require complete word/phrase match"
                  checked={formData?.exactMatch}
                  onChange={(e) => handleInputChange('exactMatch', e?.target?.checked)}
                />
              )}

              <Checkbox
                label="Active Rule"
                description="Enable this rule for automatic matching"
                checked={formData?.isActive}
                onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Pattern Testing */}
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <h3 className="text-lg font-medium text-foreground">Test Pattern</h3>
            
            <div className="flex items-end space-x-2">
              <Input
                label="Test Vendor Name"
                type="text"
                placeholder="Enter vendor name to test against patterns"
                value={testPattern}
                onChange={(e) => setTestPattern(e?.target?.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={testPatternMatching}
                iconName="Play"
                iconPosition="left"
                disabled={!testPattern?.trim()}
              >
                Test
              </Button>
            </div>

            {testResults && (
              <div className={`
                p-3 rounded-md border
                ${testResults?.matches 
                  ? 'bg-success/10 border-success/20 text-success' :'bg-error/10 border-error/20 text-error'
                }
              `}>
                <div className="flex items-center space-x-2 mb-2">
                  <Icon 
                    name={testResults?.matches ? "CheckCircle" : "XCircle"} 
                    size={16} 
                  />
                  <span className="font-medium">
                    {testResults?.matches ? 'Pattern Match Found' : 'No Pattern Match'}
                  </span>
                </div>
                
                {testResults?.matches && (
                  <div className="text-sm space-y-1">
                    <p>Matched Pattern: <code className="px-1 py-0.5 bg-background/50 rounded text-xs">{testResults?.matchedPattern}</code></p>
                    <p>Confidence: {Math.round(testResults?.confidence * 100)}%</p>
                    <p>Vendor: {formData?.vendorName}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
            >
              {rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RuleForm;
